/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useState } from 'react';
import { NodeColumn } from './NodeColumn';
import { kebabMenuIcon } from '../../../../images/images';
import { Plus } from 'sushiweb/Icons';
import { HelpDeskForm } from './HelpSectionForm';
import {
  useGetRootNodes,
  useGetCreateNodeFields,
  useGetEditNodeFields,
  useDeleteNode,
  useDuplicateNode,
} from './apis';
import grey from 'sushiweb/tokens/color/grey';
import { Button, Modal } from '../../../../UiKit';
import { Button as DesignKitButton } from '@grofers/react';
import Spinner from 'sushiweb/Spinner/Spinner';
import Shimmer from 'sushiweb/Shimmer';
import toast from 'react-hot-toast';

const nodeTypes = {
  root: 'root',
  option: 'option',
  guideline: 'guideline',
};

const MenuActionPopUp = ({
  closeActionMenuPopUp,
  menuActionHandler,
  menuActionDetails,
  isLoading,
}) => {
  return (
    <div className='tw-h-full tw-flex tw-flex-col  tw-justify-center tw-gap-14'>
      <div className='tw-text-400 tw-font-medium'>
        {`This action will ${menuActionDetails?.action} all children of the current node. Are you sure?`}
      </div>
      <div className='tw-flex tw-gap-3 tw-w-full tw-justify-end'>
        <Button
          title='Cancel'
          color='grey'
          size='compressed'
          outline={false}
          onClick={closeActionMenuPopUp}
        />
        <DesignKitButton
          variant={'fill'}
          className='tw-h-14 tw-w-28'
          onClick={menuActionHandler}
        >
          {isLoading ? (
            <Spinner size='small' color={`${grey.z300}`} />
          ) : (
            'Submit'
          )}
        </DesignKitButton>
      </div>
    </div>
  );
};

const AddNewRootNode = ({ openFormHandler, rootId = null }) => {
  const formHandler = useCallback(() => {
    openFormHandler(true, null, rootId, 'root', 1, rootId);
  }, [openFormHandler, rootId]);

  return (
    <div className='tw-flex tw-py-3 tw-px-5  tw-items-center tw-justify-start tw-gap-5 tw-bg-indigo-100 tw-p-2  tw-rounded-2xl tw-h-max tw-min-w-[30rem]'>
      <span
        className='tw-w-14 tw-h-14 tw-rounded-full tw-bg-base-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer'
        onClick={formHandler}
      >
        <Plus size={15} color={'#318616'} />
      </span>
      <div className='tw-w-4/5 tw-flex tw-justify-between'>
        <span className='tw-text-400 tw-font-extrabold '>Add New Section</span>
        <span className='tw-w-6 tw-h-6'>
          <img src={kebabMenuIcon} alt='' />
        </span>
      </div>
    </div>
  );
};

export const HelpDeskAdmin = () => {
  const [nodes, setNodes] = useState([]);
  const [showOverlay, setShowOverlay] = useState();
  const [nodeId, setNodeId] = useState();
  const [nodeNature, setNodeNature] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [rootId, setRootId] = useState(null);
  const [nodeType, setNodeType] = useState(null);
  const [formType, setFormType] = useState(null);
  const [showMenuActionPopUp, setShowMenuActionPopUp] = useState();
  const [refetchParentEnable, setRefetchParentEnable] = useState(false);

  const {
    data: rootData,
    isLoading: rootNodesLoading,
    refetch: refetchRootNodes,
    isRefetching: refetchingRootNodes,
  } = useGetRootNodes({
    onSuccess: (Nodes) => {
      setNodes(Nodes);
    },
  });

  const {
    data: formData,
    isLoading: formDataLoading,
    refetch: refetchFormData,
  } = useGetCreateNodeFields(formType, parentId, nodeNature);

  const {
    data: editNodeDetails,
    isLoading: fetchingEditNodeDetails,
    refetch: refetchEditNodeDetails,
    isRefetching: isRefetchingEditNodeDetails,
  } = useGetEditNodeFields(formType, nodeId, nodeType);

  const { mutate: deleteNode, isLoading: isDeleting } = useDeleteNode(
    { method: 'DELETE' },
    refetchRootNodes,
  );

  const { mutate: duplicateNode, isLoading: isDuplicating } = useDuplicateNode(
    {},
    refetchRootNodes,
  );

  useEffect(() => {
    if (!showOverlay) return;
    if (formType === 2) refetchEditNodeDetails();
  }, [showOverlay]);

  const handleChildNodeClick = useCallback(
    (nodeId) => {
      setNodeId(nodeId);
    },
    [nodeId, nodes],
  );

  const updateChlidHandler = (parentId, children) => {
    const updatedNode = [...nodes];
    for (let i = 0; i < nodes.length; i++) {
      if (updatedNode[i].id === parentId) {
        updatedNode[i].children = children;
      }
    }
    setNodes(updatedNode);
  };

  const openFormHandler = useCallback(
    (
      isRootType,
      parentId = null,
      rootId = null,
      nodeType = null,
      formType = null,
      nodeId = null,
    ) => {
      setShowOverlay(true);
      if (isRootType) setNodeNature('root');
      else setNodeNature('non_root');
      setParentId(parentId);
      setRootId(rootId);
      setNodeType(nodeType);
      setFormType(formType);
      setNodeId(nodeId);
    },
    [],
  );

  const closeFormHandler = useCallback(() => {
    setShowOverlay(false);
  }, []);

  const closeActionMenuPopUp = () => {
    let menuActionOption = {
      state: false,
    };
    setShowMenuActionPopUp(menuActionOption);
  };

  const handleMenuActionPopUp = (nodeId = null, action) => {
    let menuActionOption = {
      action: action,
      state: `${showMenuActionPopUp?.state ? false : true}`,
    };
    if (nodeId) menuActionOption = { ...menuActionOption, node_id: nodeId };
    setShowMenuActionPopUp(menuActionOption);
  };

  const menuActionHandler = () => {
    switch (showMenuActionPopUp?.action) {
      case 'delete':
        deleteNode(showMenuActionPopUp?.node_id);
        break;
      case 'duplicate':
        duplicateNode(showMenuActionPopUp?.node_id);
        break;
      default:
        toast.error('Something Went Wrong');
        break;
    }
    setShowMenuActionPopUp(false);
    setShowOverlay(false);
  };

  return (
    <div className='tw-w-full tw-flex tw-gap-5'>
      {rootNodesLoading || refetchingRootNodes ? (
        <div className='tw-flex tw-items-center tw-justify-center tw-w-[30rem] tw-h-[85vh] '>
          <Shimmer
            height='100%'
            width='100%'
            className='tw-bg-grey-100 tw-rounded-xl'
          />
        </div>
      ) : (
        <div className=' tw-flex tw-gap-5  '>
          {nodes?.length
            ? nodes.map((node) => {
                return (
                  <NodeColumn
                    key={node?.id}
                    node={node}
                    handleChildNodeClick={handleChildNodeClick}
                    rootId={node?.id}
                    updateChlidHandler={updateChlidHandler}
                    openFormHandler={openFormHandler}
                    refetchRootNodes={refetchRootNodes}
                    refetchParentEnable={refetchParentEnable}
                    setRefetchParentEnable={setRefetchParentEnable}
                  />
                );
              })
            : null}
        </div>
      )}

      <AddNewRootNode openFormHandler={openFormHandler} rootId={rootId} />

      {showOverlay && (
        <>
          <div
            className='tw-fixed tw-w-full tw-h-full tw-top-0 tw-left-0 tw-right-0 tw-bottom-0'
            css={`
              background-color: rgba(0, 0, 0, 0.4);
              z-index: 1;
            `}
          ></div>
          <HelpDeskForm
            closeForm={closeFormHandler}
            formFields={
              formType === 1 ? formData?.form_data : editNodeDetails?.form_data
            }
            ruleList={
              formType === 1 ? formData?.rule_list : editNodeDetails?.rule_list
            }
            rootId={rootId}
            isFormLoading={
              formDataLoading ||
              fetchingEditNodeDetails ||
              isRefetchingEditNodeDetails
            }
            refetchRootNodes={refetchRootNodes}
            nodeType={nodeType}
            nodeId={nodeId}
            parentId={parentId}
            handleMenuActionPopUp={handleMenuActionPopUp}
            nodeTypes={nodeTypes}
            setRefetchParentEnable={setRefetchParentEnable}
            path={
              nodeType === nodeTypes?.guideline
                ? editNodeDetails?.path
                : formData?.path
            }
            formType={formType}
            nodeNature={nodeNature}
          />
        </>
      )}
      {showMenuActionPopUp?.state && (
        <Modal
          isMaxContent
          isVisible={showMenuActionPopUp?.state}
          type='medium'
          handleClose={closeActionMenuPopUp}
          containerStyles={{ paddingTop: '1rem' }}
          component={
            <MenuActionPopUp
              closeActionMenuPopUp={closeActionMenuPopUp}
              menuActionHandler={menuActionHandler}
              menuActionDetails={showMenuActionPopUp}
              isLoading={isDeleting || isDuplicating}
            />
          }
        />
      )}
    </div>
  );
};
